import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { DropdownCategory, DropdownOptionResponse } from "../client-file/api/ClientFileApi";
import { useLookupsApi } from "../client-file/api/ClientFileApiContext";
import { ClientFileQueryKey } from "../client-file/query/clientFileQueryKeys";

const TWO_HOURS = 2 * 60 * 60 * 1000;

export const dropdownQueryKey = [ClientFileQueryKey.DropdownOptions];

export type DropdownOptions = ReturnType<typeof useDropdownOptions>;

/**
 * Fetches all dropdown options in a single request and returns them aliased by category.
 * The result is cached for 2 hours and shared across all consumers.
 * Dropdowns whose values application logic references are not here — read those through
 * `useSystemDropdownOptions`, which yields key/label pairs instead of plain labels.
 */
export function useDropdownOptions() {
  const lookupsApi = useLookupsApi();

  const { data } = useQuery<DropdownOptionResponse[]>({
    queryKey: dropdownQueryKey,
    queryFn: () => lookupsApi.getAllDropdownOptions(),
    staleTime: TWO_HOURS,
  });

  return useMemo(() => {
    const grouped = Object.fromEntries(Object.values(DropdownCategory).map((cat) => [cat, [] as string[]])) as Record<
      DropdownCategory,
      string[]
    >;

    for (const option of data ?? []) {
      if (option.category in grouped) {
        grouped[option.category].push(option.value);
      }
    }

    return {
      salutations: grouped[DropdownCategory.Salutation],
      titles: grouped[DropdownCategory.Title],
      citizenships: grouped[DropdownCategory.Citizenship],
      mediaInquiries: grouped[DropdownCategory.MediaInquiry],
      contactSources: grouped[DropdownCategory.ContactSource],
      countries: grouped[DropdownCategory.Country],
      cities: grouped[DropdownCategory.City],
      assignmentPriorities: grouped[DropdownCategory.AssignmentPriority],
      caseNoteLocations: grouped[DropdownCategory.CaseNoteLocation],
      appointmentLocations: grouped[DropdownCategory.AppointmentLocation],
      employmentStatuses: grouped[DropdownCategory.EmploymentStatus],
      caseNoteResults: grouped[DropdownCategory.CaseNoteResult],
      qualifications: grouped[DropdownCategory.Qualification],
      maritalStatuses: grouped[DropdownCategory.MaritalStatus],
      livingSituations: grouped[DropdownCategory.LivingSituation],
      clientGoalsAndResults: grouped[DropdownCategory.ClientGoalAndResult],
      clientMeasures: grouped[DropdownCategory.ClientMeasures],
      clientCauses: grouped[DropdownCategory.ClientCauses],
      claimLegalBases: grouped[DropdownCategory.ClaimLegalBasis],
      teams: grouped[DropdownCategory.Team],
      workAreas: grouped[DropdownCategory.WorkArea],
      srvCounselingTypes: grouped[DropdownCategory.SrvCounselingType],
      srvCourseEntryResults: grouped[DropdownCategory.SrvCourseEntryResult],
      identityDocumentTypes: grouped[DropdownCategory.IdentityDocumentType],
    };
  }, [data]);
}
